import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { DataStats } from "@/components/data-stats/DataStats";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { listCompanies, enqueueCompanyEnrichment, enqueueCompanyJobsScraping, PaginatedCompaniesResponse, CompanyListItem } from "@/services/companiesApi";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import EnrichStaleDialog from "@/components/EnrichStaleDialog";
import { toast } from "@/hooks/use-toast";

type SortBy = "created" | "updated" | "name";

function SortHeader({
  label,
  field,
  current,
  order,
  onSort,
}: {
  label: string;
  field: SortBy;
  current: SortBy;
  order: string;
  onSort: (field: SortBy) => void;
}) {
  const active = current === field;
  return (
    <th
      className="px-3 py-2 text-left cursor-pointer select-none hover:bg-gray-100"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active ? (
          order === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 text-muted-foreground" />
        )}
      </div>
    </th>
  );
}

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<PaginatedCompaniesResponse | null>(null);
  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(25);
  const [sortBy, setSortBy] = useState<SortBy>("created");
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listCompanies(navigate, { page, size, sort_by: sortBy, sort_order: sortOrder });
        setData(res);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Erreur inconnue");
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, page, size, sortBy, sortOrder]);

  const handleSort = (field: SortBy) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder(field === "name" ? "asc" : "desc");
    }
    setPage(1);
  };

  const pageIds = useMemo(() => new Set((data?.items ?? []).map((c) => c.id)), [data]);
  const selectedCount = useMemo(
    () => Object.entries(selected).filter(([id, v]) => v && pageIds.has(Number(id))).length,
    [selected, pageIds]
  );
  const allSelectedOnPage = useMemo(
    () => (data?.items?.length ?? 0) > 0 && (data?.items ?? []).every((c) => selected[c.id]),
    [data, selected]
  );

  function toggleOne(id: number) {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleAllOnPage() {
    if (!data?.items?.length) return;
    const next = { ...selected } as Record<number, boolean>;
    const newVal = !allSelectedOnPage;
    for (const c of data.items) next[c.id] = newVal;
    setSelected(next);
  }

  const onEnqueue = async (c: CompanyListItem) => {
    try {
      const res = await enqueueCompanyEnrichment(navigate, c.id);
      toast({
        title: c.name,
        description: res.status === "already_queued" ? "Déjà en file" : "Ajouté à la file d'enrichissement",
      });
    } catch (e) {
      toast({ title: c.name, description: e instanceof Error ? e.message : "Erreur", variant: "destructive" });
    }
  };

  const onEnqueueJobs = async (c: CompanyListItem) => {
    try {
      const res = await enqueueCompanyJobsScraping(navigate, c.id);
      toast({
        title: c.name,
        description: res.status === "already_queued" ? "Déjà en file" : "Scraping offres ajouté",
      });
    } catch (e) {
      toast({ title: c.name, description: e instanceof Error ? e.message : "Erreur", variant: "destructive" });
    }
  };

  async function onBulkEnqueue() {
    if (!data?.items?.length) return;
    const ids = data.items.filter((c) => selected[c.id]).map((c) => c.id);
    if (!ids.length) return;
    setBulkLoading(true);
    let ok = 0;
    let already = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const res = await enqueueCompanyEnrichment(navigate, id);
        if (res.status === "already_queued") already += 1; else ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkLoading(false);
    setSelected((prev) => {
      const copy = { ...prev };
      for (const id of ids) delete copy[id];
      return copy;
    });
    toast({
      title: "Mise en file terminée",
      description: `${ok} ajoutée(s), ${already} déjà présente(s), ${failed} échec(s).`,
      variant: failed > 0 ? "destructive" : "default",
    });
  }

  async function onBulkEnqueueJobs() {
    if (!data?.items?.length) return;
    const ids = data.items.filter((c) => selected[c.id]).map((c) => c.id);
    if (!ids.length) return;
    setBulkLoading(true);
    let ok = 0;
    let already = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        const res = await enqueueCompanyJobsScraping(navigate, id);
        if (res.status === "already_queued") already += 1; else ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkLoading(false);
    setSelected((prev) => {
      const copy = { ...prev };
      for (const id of ids) delete copy[id];
      return copy;
    });
    toast({
      title: "Scraping offres",
      description: `${ok} ajoutée(s), ${already} déjà présente(s), ${failed} échec(s).`,
      variant: failed > 0 ? "destructive" : "default",
    });
  }

  const canPrev = page > 1;
  const canNext = data ? page < data.pages : false;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Companies</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <DataStats entity="companies" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold">Liste des entreprises</h2>
              <EnrichStaleDialog entity="companies" label="Entreprises" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-600">Taille page</label>
              <select
                className="border rounded px-2 py-1 text-sm"
                value={size}
                onChange={(e) => { setSize(Number(e.target.value)); setPage(1); }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          {error && <div className="text-red-600 text-sm">{error}</div>}
          {loading && <div className="text-gray-500 text-sm">Chargement...</div>}

          <div className="overflow-auto border rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left w-10">
                    <input
                      type="checkbox"
                      aria-label="Tout selectionner"
                      checked={allSelectedOnPage}
                      onChange={toggleAllOnPage}
                    />
                  </th>
                  <th className="px-3 py-2 text-left">ID</th>
                  <SortHeader label="Nom" field="name" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <th className="px-3 py-2 text-left">External ref</th>
                  <th className="px-3 py-2 text-left">Profil</th>
                  <SortHeader label="Cree" field="created" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <SortHeader label="Modifie" field="updated" current={sortBy} order={sortOrder} onSort={handleSort} />
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.items?.map((c) => (
                  <tr key={c.id} className="border-t hover:bg-muted/50 cursor-pointer" onClick={() => navigate(`/companies/${c.id}`)}>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={!!selected[c.id]}
                        onChange={() => toggleOne(c.id)}
                        aria-label={`Selectionner ${c.name}`}
                      />
                    </td>
                    <td className="px-3 py-2">{c.id}</td>
                    <td className="px-3 py-2 font-medium text-blue-600">{c.name}</td>
                    <td className="px-3 py-2 text-xs text-gray-600 font-mono">{c.external_ref ?? "—"}</td>
                    <td className="px-3 py-2">
                      {c.profile_url ? (
                        <a href={c.profile_url} target="_blank" rel="noreferrer" className="text-blue-600 underline" onClick={(e) => e.stopPropagation()}>
                          Voir le profil
                        </a>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{c.created ?? ""}</td>
                    <td className="px-3 py-2">{c.updated ?? ""}</td>
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        <Button size="sm" onClick={() => onEnqueue(c)}>Enrichir</Button>
                        <Button size="sm" variant="outline" onClick={() => onEnqueueJobs(c)}>Offres</Button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!data?.items?.length && !loading && (
                  <tr>
                    <td className="px-3 py-4 text-center text-gray-500" colSpan={8}>Aucune entreprise</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {selectedCount > 0 && (
            <div className="sticky bottom-4 left-0 right-0 flex items-center justify-between gap-4 bg-white border rounded px-4 py-3 shadow">
              <div className="text-sm">
                {selectedCount} selectionnee(s) sur cette page
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" onClick={() => setSelected((prev) => {
                  const copy: Record<number, boolean> = { ...prev };
                  for (const id of pageIds) delete copy[id as number];
                  return copy;
                })} disabled={bulkLoading}>
                  Effacer la selection
                </Button>
                <Button onClick={onBulkEnqueue} disabled={bulkLoading}>
                  {bulkLoading ? "Envoi..." : "Enrichir"}
                </Button>
                <Button variant="outline" onClick={onBulkEnqueueJobs} disabled={bulkLoading}>
                  {bulkLoading ? "Envoi..." : "Scraper offres"}
                </Button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Page {data?.page ?? page} / {data?.pages ?? 1} — {data?.total ?? 0} elements
            </div>
            <div className="flex gap-2">
              <Button variant="outline" disabled={!canPrev} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Precedent
              </Button>
              <Button variant="outline" disabled={!canNext} onClick={() => setPage((p) => p + 1)}>
                Suivant
              </Button>
            </div>
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
